export default {
    "navigation": {
        "allWatchfaces": "All Apps",
        "faq": "FAQ",
        "helpCenter": "Help & Instructions",
        "newsletter": "Newsletter",
        "contact": "Contact",
        "weatherProvider": "Weather Provider",
        "tides": "Tides",
        "colors": "Colors",
        "fonts": "Fonts"
    },
    "homepage": {
        "title": "TitanicTurtle Apps",
        "subtitle": "Modern Garmin apps with advanced features for all your activities",
        "features": {
            "tides": "🌊 Tides",
            "weather": "🌤️ Weather",
            "themes": "🎨 Color Themes",
            "gps": "📍 GPS"
        },
        "availableWatchfaces": "Available Watchfaces",
        "newBadge": "New",
        "loading": "Loading watchfaces...",
        "error": "Error loading",
        "retry": "Try again",
        "filter": {
            "label": "Product filter",
            "watchfaces": "Watchfaces",
            "apps": "Apps",
            "all": "All",
            "noProducts": "No products in this category"
        }
    },
    "watchface": {
        "purchase": "Buy Now",
        "buy": "Buy",
        "features": "Features",
        "colorThemes": "Color Themes",
        "themeDescription": "Click on a theme to copy the theme string and use it in your watchface settings.",
        "dynamicThemeSwitching": "Dynamic Theme Switching",
        "sunsetSwitching": {
            "title": "🌅 After Sunset / Before Sunrise",
            "description": "Put an exclamation mark ! in front of the theme string to automatically switch after sunset (example: !UEUE[UEUEEEEEEU^)."
        },
        "lowPowerSwitching": {
            "title": "🔋 In Low Power Mode",
            "description": "Put a hash # in front of the theme string for low power mode (example: #UEUE[UEUEEEEEEU^)."
        },
        "themeCopied": "Theme string copied to clipboard!",
        "viewDetails": "View Details"
    },
    "faq": {
        "title": "Frequently Asked Questions",
        "subtitle": "Find answers to common questions about our watchfaces",
        "tabs": {
            "search": "Search",
            "categories": "Categories",
            "all": "Show All FAQs"
        },
        "category": {
            "selected": "Selected Category",
            "showing": "Showing",
            "items": "items",
            "from": "from",
            "clear": "Clear Selection",
            "back": "Back to Categories"
        },
        "viewAnswer": "View Answer",
        "closeAnswer": "Close Answer",
        "darkMode": {
            "enable": "Enable Dark Mode",
            "disable": "Disable Dark Mode",
            "light": "Light",
            "dark": "Dark"
        },
        "search": {
            "placeholder": "Search for help...",
            "button": "Search",
            "suggestions": "Popular searches",
            "tips": {
                "title": "Search Tips",
                "keywords": "Use specific keywords like \"GPS\", \"weather\", or \"settings\"",
                "language": "Search works in your selected language",
                "categories": "Browse categories for organized help"
            }
        },
        "noResults": {
            "title": "No results found",
            "description": "Try different keywords or browse categories",
            "browseCategories": "Browse Categories"
        },
        "suggestions": {
            "title": "Popular searches"
        },
        "trending": {
            "title": "Trending searches"
        },
        "categories": {
            "title": "Browse by Category",
            "items": "items",
            "allItems": "All FAQ Items",
            "allItemsDescription": "Browse all frequently asked questions",
            "noItems": {
                "title": "No items in this category",
                "description": "Try selecting a different category"
            },
            "viewMore": "View All Items"
        },
        "results": {
            "title": "Search Results",
            "found": "results found for",
            "total": "Total Results",
            "bestMatch": "Best Match",
            "categories": "Categories",
            "sortBy": "Sort by",
            "sort": {
                "relevance": "Relevance",
                "priority": "Priority",
                "category": "Category"
            },
            "lastUpdated": "Last updated",
            "matchedIn": "Matched in",
            "footer": "Didn't find what you were looking for?",
            "browseCategories": "Browse Categories",
            "viewTrending": "View Trending",
            "contactSupport": "Contact Support"
        },
        "actions": {
            "copy": "Copy",
            "share": "Share",
            "copyAnswer": "Copy Answer",
            "copyAll": "Copy All"
        },
        "loading": "Loading FAQs...",
        "error": "Error loading",
        "retry": "Try again",
        "questions": {
            "gpsFix": {
                "question": "What is a GPS fix and how can I do it? / Why do some options show 'GPS?'",
                "answer": "A GPS fix is needed to let the watchface know where you are to calculate everything regarding suntimes and moontimes correctly.\n\nA GPS fix can be done by starting an activity and wait until the watch got the GPS signal or by long pressing the GPS button and wait until the watch got the GPS signal.\n\nAfter that you can return to the watchface and everything should show up correctly."
            },
            "userData": {
                "question": "Do the watchfaces collect any userdata?",
                "answer": "All TitanicTurtle watchfaces do not collect any userdata and do not send it elsewhere. They only display data provided by Garmin or do some calculations based on it (like active calories).\n\nIn general, all data provided by Garmin to 3rd party watchfaces is not personalized - there is no name or similar to be read out for example."
            },
            "permissions": {
                "question": "Why do the watchfaces need so much permissions?",
                "answer": "TitanicTurtle Watchfaces need permissions to provide the large selection of data / functions.\n\nHere is every single one explained:\n\n<b>Run in the background when it is not active</b> - this is needed to update tide / weather data from third party providers in combination with the internet permission\n\n<b>Send/receive information to/from the Internet</b> - this is needed to update tide / weather data in combination with running in the background.\n\n<b>Read and display data from other apps.</b> - Other apps must explicitly provide data to be read by Watchfaces. This includes Garmin's own apps like Stress or BodyBattery, which can be updated in real time. Unauthorized data from other apps cannot be read.\n\n<b>GPS location</b> - this is needed to calculate times like sunrise/sunset, moonrise/moonset and get the correct weather location\n\n<b>Heart rate, barometer, temperature, and altitude history</b> - data that will be displayed on the watchface - for display purposes only.\n\n<b>Your Garmin Connect™ fitness profile</b> - This is needed to display weight, calculate active calories and activate energy saving options to the set sleep times.\n\nThere is no personal data like name, addresses or anything like this to be read out by watchfaces.\n\nUnfortunately these permissions can't be set in the way you use the watchface and are always required."
            },
            "proFeatures": {
                "question": "What does ♛ mean and why do some views show 'key'?",
                "answer": "♛ marks the pro features of this watchface. To unlock them after your 5 days trial you can buy an unlock key.\n\n'key?' means that your trial period is over."
            },
            "temperatureUnits": {
                "question": "How do I change the temperature units?",
                "answer": "Change it by entering your watches system settings (long press the menu button): Navigate to System -> Format -> Units and change it there"
            },
            "timeFormat": {
                "question": "How do I set the 12h/24h Format?",
                "answer": "Change it by entering your watches system settings (long press the menu button): Navigate to System -> Format -> Timeformat and change it there"
            },
            "distanceUnits": {
                "question": "How do I change the units for distances or heights?",
                "answer": "Change it by entering your watches system settings (long press the menu button): Navigate to System -> Format -> Units and change it there"
            }
        }
    },
    "helpCenter": {
        "title": "Help & Instructions",
        "subtitle": "Find answers, guides, and instructions for your watchface",
        "guidesTitle": "Guides & Instructions",
        "faqTitle": "Frequently Asked Questions",
        "allCategories": "All",
        "guides": "Guides",
        "faqResults": "FAQ",
        "noResults": "No results found",
        "noResultsHint": "Try different keywords or browse the categories and guides below."
    },
    "guides": {
        "backToHelp": "Back",
        "purchase": {
            "title": "Purchase & Installation",
            "tabs": {
                "installation": "Installation",
                "versions": "VX / Elite Versions",
                "troubleshooting": "Troubleshooting"
            },
            "installation": {
                "title": "Install watchfaces via ConnectIQ App",
                "intro": "Watchfaces can be installed on the watch via the Garmin ConnectIQ app for Android or iOS.",
                "step1": "Connect your watch to your phone via Bluetooth and open the ConnectIQ app on the phone.",
                "step2": "Navigate to the page of the watchface you want to install.",
                "step3": "Tap the \"Install\" button. The watchface will be placed in the download queue and installed on your watch.",
                "step4": "After installation you will see a message in the app or on the watch and the watchface will be available on your device.",
                "noteScreen": "Make sure that the screen of your phone does not turn off and that the ConnectIQ app remains open during installation and updates.",
                "selectWatchfaceTitle": "Selecting the watchface on the watch",
                "selectWatchfaceIntro": "If the watchface is installed but not yet visible, you may need to select it in the system menu of your watch.",
                "select1": "Press and hold the menu button on your watch (or the corresponding button on touchscreen devices) to open the system menu.",
                "select2": "Navigate to the menu item \"Watch Faces\" or \"Display Designs\".",
                "select3": "You will see a list of all installed watchfaces. Use the up/down buttons or the touchscreen to navigate.",
                "select4": "Confirm the selected watchface with the top-right button. The watchface is now active on your watch."
            },
            "versions": {
                "title": "VX vs Elite – what is the difference?",
                "sameFeatures": "Technically, both versions are identical and offer the same features. The only difference is how they are purchased and activated.",
                "eliteTitle": "Elite Version (Garmin Pay)",
                "elite1": "Separate installation of the watchface purchased directly via Garmin Pay in the ConnectIQ Store.",
                "elite2": "The Elite version is bound to your Garmin account. If you buy a new watch, you can install the Elite watchface again without an extra key.",
                "elite3": "Garmin Pay is not supported on all watch models and may not be available in all countries.",
                "eliteNoKey": "Important: When purchasing an Elite version via Garmin Pay, no unlock code is sent. The Elite version is unlocked directly after purchase and does not require a code.",
                "vxTitle": "VX / Website Version",
                "vx1": "Compatible with a larger number of devices and often available even when Garmin Pay is not.",
                "vx2": "Includes a built-in free trial period of several days.",
                "vx3": "After the trial, you can purchase an unlock code on this website to permanently unlock the VX version (or its pro features).",
                "vx4": "The unlock code is always valid only for the device for which it was purchased (WatchfaceID).",
                "vx5": "If your watch is replaced by Garmin under warranty, a new key can be generated. In this case, please contact support via the contact form."
            },
            "website": {
                "title": "Purchase via website (VX / normal versions)",
                "step1": "Install the watchface on your watch first (see Installation tab). The WatchfaceID only appears in the watchface settings after installation.",
                "step2": "Open the watchface settings (via ConnectIQ app or Garmin Express) and copy the WatchfaceID from the top field.",
                "step3": "On this website, select the corresponding watchface and enter your WatchfaceID in the purchase form.",
                "step4": "Complete the payment. You will receive the unlock code via email and can enter it in the key field of the watchface settings.",
                "noteNoAutoInstall": "Important: Purchasing an unlock code via the website does not automatically install the watchface on your watch. The installation steps above must be completed manually."
            },
            "troubleshooting": {
                "installationTitle": "Installation issues (queue / stuck updates)",
                "installationIntro": "Sometimes installation fails, the watchface stays in the queue for a long time, or the ConnectIQ app reports endless updates.",
                "installationCause": "This usually relates to the ConnectIQ app or Garmin services and not to the watchface itself.",
                "installationFix1": "Reboot your watch and restart the ConnectIQ app on your phone.",
                "installationFix2": "Try the installation again and make sure that the phone screen does not turn off and the ConnectIQ app stays open during the process.",
                "garminPayTitle": "Garmin Pay purchase stuck \"in progress\"",
                "garminPayText": "If a Garmin Pay purchase in the ConnectIQ store is stuck in an \"in progress\" state for a long time, this is handled entirely by Garmin. As a developer, we have no influence on the Garmin Pay system. In this case, please contact the official Garmin support.",
                "noKeyTitle": "I did not receive an unlock key after website purchase",
                "noKey1": "First, check the spam folder of your email inbox. Emails with keys often end up there.",
                "noKey2": "In many cases, a typo in the email address prevents delivery. If you cannot find the email, please contact us.",
                "noKey3": "and include the email address you used for payment and, if possible, the WatchfaceID.",
                "noKeyElite": "Note: When purchasing an Elite version directly via Garmin Pay in the ConnectIQ store, no unlock code is sent. The Elite version is unlocked immediately after purchase and does not require a code."
            },
            "contactLink": "Contact form"
        },
        "timezones": {
            "title": "Time zones & UTC offset",
            "basicsTitle": "What are the 2nd and 3rd time zones?",
            "basicsText": "In many watchfaces you can configure two additional time zones. They are usually called \"Second time zone\" and \"Third time zone\" and are shown in dedicated fields on the watchface.",
            "offsetTitle": "Configuring the UTC offsets",
            "offsetIntro": "In the watchface settings there is usually a field where you can enter the offsets of the second and third time zone in minutes relative to UTC.",
            "offsetFormat": "The first value is the offset for the second time zone, the second value is the offset for the third time zone. Values are given in minutes, separated by a space. A plus sign is not added automatically, so positive values must be entered without +.",
            "exampleLabel": "Example:",
            "exampleExplanation": "Here the second time zone uses an offset of -4.5 hours (−270 minutes) and the third time zone an offset of +2 hours (+120 minutes), always relative to UTC.",
            "changeExample": "If you want to change the second time zone to +6 hours, the first value must be 360 (6 × 60). With the example above this would result in:",
            "advancedTitle": "Advanced options (A/B/C suffix)",
            "advancedOptionalHint": "This section is optional. Only relevant if your watchface supports the A/B/C format suffixes.",
            "advancedIntro": "Whether your watchface supports these suffixes is stated in the description text above the offset field in the watchface settings. If supported, you may see a description similar to:",
            "advancedExample": "UTC 2nd + 3rd time zone offset in minutes (separate with space) (letters after the offsets: A - 12h format, B - 24h format, C - military format, empty - standard)",
            "advancedNote": "These optional letters (A/B/C) control the display format of the respective time zone on some watchfaces. Only use them if the specific watchface description mentions them and you need a different time format. For most users the plain numeric offsets without letters are sufficient.",
            "exampleSectionTitle": "Step-by-step: Example for different cities and formats",
            "exampleIntro": "The following example shows how to set a second time zone (e.g. New York) and a third time zone (e.g. Tokyo), and optionally choose 12h or 24h display for each.",
            "exampleStep1Title": "Step 1: UTC offset for the 2nd time zone",
            "exampleStep1Text": "Decide which city or region you want for the second time zone. Convert its time difference to UTC into minutes. Example: New York (EST, UTC−5) → −300 minutes. Enter this as the first value in the settings field.",
            "exampleStep2Title": "Step 2: UTC offset for the 3rd time zone",
            "exampleStep2Text": "Do the same for the third time zone. Example: Tokyo (JST, UTC+9) → 540 minutes. This is the second value, separated by a space from the first.",
            "exampleOnlyOffsets": "If your watchface does not use A/B/C suffixes, you are done. Enter:",
            "exampleStep3Title": "Step 3: Optional format suffix (A/B/C)",
            "exampleStep3Optional": "This step is optional: you can leave out the suffixes even if your watchface supports them; then the default format is used. If you want to force 12h, 24h, or military format per zone, add the letter directly after the offset — no space between offset and suffix.",
            "exampleStep3Text": "Example: 12-hour format for New York (A) and 24-hour for Tokyo (B):",
            "exampleStep3Note": "No suffix means the watchface default (often 24h). Whether suffixes are supported is indicated in the description above the offset field in the watchface settings.",
            "exampleFormatTitle": "How the time is displayed with A, B, C",
            "exampleFormatIntro": "For the same moment in time, the display differs by format (example: 2nd zone New York -300, 3rd zone Tokyo 540). Live preview:",
            "zone2": "2nd zone",
            "zone3": "3rd zone",
            "exampleTableTitle": "Reference: UTC offsets for selected cities (minutes)",
            "exampleTableIntro": "Standard time; summer time may differ. Multiply hours by 60."
        },
        "switchViews": {
            "title": "Switchviews & gesture control",
            "wordLabel": "switch view",
            "basicsTitle": "What are Switchviews?",
            "basicsText": "Many watchfaces can show different information in the same area of the screen. These alternative pages are often called \"Switchview\" ({{wordLabel}}), \"Switchview 2\", \"Top View 1/2\", \"Bottom View 1/2\" or similar in the watchface settings.",
            "proNote": "In many cases these additional views are pro features that are available during a trial period and require an activation key afterwards.",
            "gestureTitle": "Switching views with a wrist gesture",
            "gestureIntro": "On watches without a compatible touchscreen, the alternative views can be changed using a second wrist movement.",
            "gestureStep1": "Raise your wrist as usual to wake up the display. The seconds or the brightened screen indicate that the watch is in active mode.",
            "gestureStep2": "Within a few seconds (about 7 seconds), perform the wrist movement again – not too abruptly, but clearly enough so that the watch recognizes it as a second gesture.",
            "gestureStep3": "If the watchface supports Switchviews, the configured areas (for example top or bottom view) will change to the next page.",
            "gestureHint": "If nothing changes, check the watchface settings: some views must be explicitly assigned (e.g. \"Top View 2\" / \"Bottom View 2\").",
            "touchTitle": "Switchviews on touchscreen devices",
            "touchIntro": "On newer watches with a compatible touchscreen (e.g. Fenix 7 and newer), views can often also be changed by pressing specific touch zones.",
            "touchConfig": "In many watchfaces you can configure these zones in the settings, often further down in the list. Typical examples are the hour and minute digits of the time or separate areas on the display.",
            "touchHold": "If a touch zone is configured, a long press on the respective digit/area will switch the assigned view.",
            "touchMultiple": "Some watchfaces offer several zones that can be assigned separately – for example one zone for the top view and one for the bottom view.",
            "touchInfo": "If special zones exist for a watchface, they are usually described in the info text of the watchface on this website or in the ConnectIQ Store."
        },
        "findWfId": {
            "title": "How to find your WatchfaceID",
            "whatIs": "What is the WatchfaceID?",
            "whatIsExplanation": "Each watchface has an individual WatchfaceID for each device. It is a 5-6 digit number. This ID is required for purchasing an activation code or for support purposes.",
            "connectIqTitle": "Via ConnectIQ App (recommended)",
            "ciq1": "Make sure your watch is connected to your phone via Bluetooth.",
            "ciq2": "Open the ConnectIQ app on your phone.",
            "ciq3": "Navigate to the watchface page where you can see the description and pictures. If you cannot find it, use the \"Search\" bar at the bottom or look under \"My watch faces\" in the \"Device\" section.",
            "ciq4": "Press the \"Settings\" button on the watchface page to open all settings.",
            "ciq5": "The WatchfaceID is shown in the top field. Copy or type this number - this is the ID required for payment.",
            "keyEntryTitle": "Entering the activation key",
            "keyEntryExplanation": "In the line below the WatchfaceID field you will find the field for the key/code that is sent after payment. Some watchfaces display remaining trial days there - delete this before entering your key.",
            "garminExpressTitle": "Via Garmin Express (Desktop)",
            "ge1": "Install Garmin Express and connect your watch to the computer.",
            "ge2": "Go to the list of installed watchfaces.",
            "ge3": "Click the 3-dot button next to the watchface entry to open settings.",
            "ge4": "Follow the same steps as with the ConnectIQ app.",
            "geNote": "Note: Using the ConnectIQ app is recommended, as some Garmin Express versions can cause errors.",
            "zeroId": "WatchfaceID shows 0?",
            "zeroIdExplanation": "Sometimes the WatchfaceID shows 0. This can usually be fixed by saving the settings once or restarting the watch. If it still shows 0 after that, it should still work for payment."
        },
        "location": {
            "title": "GPS & Location Setup",
            "whatIsGpsFix": "What is a GPS Fix?",
            "gpsFixExplanation": "A GPS fix tells your watchface where you are so it can calculate sunrise/sunset times, moon phases, weather location, and tides correctly.",
            "howTo": "How to perform a GPS Fix",
            "step1": "On your watch, navigate to the start screen of any activity (e.g. \"Running\")",
            "step2": "Wait until the watch acquires a GPS signal (green bar/icon)",
            "step3": "Return to the watchface - data should update within a few minutes",
            "alternative": "Alternative: Phone GPS",
            "alternativeExplanation": "You can also activate GPS on your phone. The watch must be connected via Bluetooth. This method is slower but works just as well.",
            "requirements": "Requirements",
            "req1": "Watch connected to phone via Bluetooth",
            "req2": "Garmin Connect app (not ConnectIQ) running at least in background",
            "req3": "GPS enabled on phone for phone-based location"
        },
        "settings": {
            "tabs": {
                "options": "Data Field Options",
                "change": "Change Settings",
                "troubleshoot": "Troubleshooting"
            },
            "title": "Watchface Settings",
            "optionsIntro": "This is the general list of all data field options. Click on an option to see its description. Note that not all options are available for all watchfaces, or may differ slightly in name.",
            "change": {
                "title": "How to change watchface settings",
                "intro": "Watchface settings can be changed via the ConnectIQ app on your phone.",
                "step1": "Connect your watch to your phone via Bluetooth and open the ConnectIQ app (not the Connect app).",
                "step2": "Navigate to the watchface page. If it is not installed yet, install it first.",
                "step3": "On the watchface page you will find a \"Settings\" button – tap it to open the full list of watchface settings.",
                "step4": "All data fields, colors, and advanced options can be changed here.",
                "step5": "Tap the save button in the top right corner to transfer the settings to your watch. The settings list will close after saving.",
                "installLink": "Installation guide"
            },
            "troubleshoot": {
                "title": "Troubleshooting",
                "cantSave": {
                    "q": "Settings cannot be saved – error message appears",
                    "a1": "Sometimes Garmin servers have trouble processing certain characters.",
                    "a2": "Try clearing the color theme field (if present) in the settings, then save again. This usually resolves the issue."
                },
                "wontOpen": {
                    "q": "Settings won't open",
                    "a1": "Fully close the ConnectIQ app and restart your watch if necessary.",
                    "a2": "If you only see an \"Install\" button, the watchface is not yet installed on your watch."
                },
                "missingOptions": {
                    "q": "I cannot find a specific setting option",
                    "a": "Not all options are available on all devices. The available options depend on your watch model and the specific watchface."
                },
                "naValue": {
                    "q": "A data field shows only \"n/a\"",
                    "a": "Your device does not support this option – usually because it is an older model or lacks the required hardware (e.g. barometer, pulse oximeter)."
                }
            }
        },
        "dateFormat": {
            "title": "Custom date format (Date 1 / 2 / 3)",
            "basicsTitle": "What are the date format fields?",
            "basicsIntro": "In the watchface settings there are two fields (or three, if the watchface supports it) that let you configure how Date 1, Date 2 and optionally Date 3 are displayed. Depending on which letters you enter in each field, the corresponding date option is rendered in that format.",
            "notAll": "This is not available on all watchfaces.",
            "where": "The fields to configure the date format are usually located further down in the watchface settings list, below the data field options.",
            "codesTitle": "Format codes",
            "codesIntro": "You can use the following letters in the format field. The output will reflect the current date.",
            "codeD": "day of month (with leading zero)",
            "codeDText": "day as text (e.g. Monday)",
            "codeM": "month numeric (with leading zero)",
            "codeMText": "month as text",
            "codeY": "two digit year",
            "codeY4": "four digit year",
            "codeJ": "Julian date (military)",
            "codeW": "week number",
            "codeO": "month without leading zero",
            "codeN": "day without leading zero",
            "codeP": "day of the year",
            "exampleHint": "Example: \"D, d.M.Y\" could display \"Mon, 27.02.2026\"."
        }
    },
    "newsletter": {
        "title": "Newsletter",
        "description": "Subscribe to receive updates about new watchfaces, features, and promotions.",
        "frequencyHint": "Roughly one email every two months. You can unsubscribe at any time.",
        "emailPlaceholder": "Your email address",
        "subscribe": "Subscribe",
        "unsubscribe": "Unsubscribe",
        "subscribeSuccess": "Successfully subscribed! Please check your email.",
        "subscribeError": "An error occurred. Please try again later.",
        "unsubscribeSuccess": "Successfully unsubscribed.",
        "unsubscribeError": "An error occurred. Please try again later."
    },
    "contact": {
        "title": "Contact",
        "description": "Have a question or need support? Send me a message.",
        "emailLabel": "Email",
        "emailPlaceholder": "your@email.com",
        "deviceLabel": "Device (optional)",
        "devicePlaceholder": "e.g. Fenix 7",
        "watchfaceLabel": "Watchface (optional)",
        "watchfacePlaceholder": "e.g. Horizon",
        "messageLabel": "Message",
        "messagePlaceholder": "Your message...",
        "send": "Send Message",
        "sendAnother": "Send another message",
        "successTitle": "Message sent!",
        "successMessage": "I will get back to you as soon as possible.",
        "error": "An error occurred. Please try again or email dev@orcatec.net directly.",
        "rateLimit": "Maximum 2 messages per 5 minutes. Please try again later.",
        "consentNotice": "By submitting, you agree to the processing of your data in accordance with our",
        "guidesIntro": "If you have questions about specific features, these guides might already contain the answers:",
        "faqIntro": "For many topics there are also detailed FAQs:",
        "faqLinkLabel": "Open Help & Instructions",
        "responseTimeNotice": "Please understand that during busy times it can sometimes take several days until I can reply."
    },
    "refund": {
        "title": "Refund Policy"
    },
    "terms": {
        "title": "Terms of Service"
    },
    "privacy": {
        "title": "Privacy Notice"
    },
    "footer": {
        "description": "Modern Garmin apps with advanced features for all activities.",
        "tagline": "Modern Garmin apps with advanced features for all activities.",
        "linksTitle": "Links",
        "socialsTitle": "Socials",
        "terms": "Terms of Service",
        "impressum": "Imprint",
        "privacy": "Privacy",
        "refund": "Refund Policy",
        "contact": "Contact",
        "rights": "All rights reserved.",
        "links": "Links",
        "contactDescription": "Questions or Support?",
        "contactLink": "Get in touch",
        "copyright": "Orcatec - TitanicTurtle. All rights reserved.",
        "garminForums": "Garmin Forums"
    },
    "tides": {
        "title": "Tides via Stormglass.io",
        "intro": "If you want to use the tides feature on a watchface using stormglass as a provider, you need to register on their website to retrieve an API Key which you can enter in the specific field in your watchface settings.",
        "apiKey": {
            "title": "Copy API Key",
            "description": "When you copy your APIkey from anywhere make sure that all characters are included (usually over 60) - sometimes Android/IOS cuts away a part after a \"-\"."
        },
        "freePlan": {
            "title": "Free Plan",
            "description": "The free (stormglass io) plan features 10 free requests a day which is enough to provide tide data to supported TitanicTurtle watchfaces.",
            "note": "(If your watchface supports stormglass io as a weather provider and you plan to use it, you might need another subscription plan)"
        },
        "dataDownload": {
            "title": "Data Download",
            "description": "Usually, tide data is being downloaded for multiple days and refreshed every day if there is a phone connection available. So even if you do not have an internet connection expect the tide data to be displayed at least for 3 (Horizon) / 5 (Centurion) days. (depends on the watchface / device)"
        },
        "initialSetup": {
            "title": "Initial Setup",
            "description": "As soon as you have entered the stormglass io API key in the settings, it might take up to 30 minutes until the data is being downloaded and available."
        },
        "locationUpdate": {
            "title": "Location Update",
            "description": "If you change your position be sure to get a gps signal on your watch so that the tide data can be updated for the new location."
        },
        "requirements": {
            "title": "Requirements",
            "bluetooth": "Make sure your watch is connected via bluetooth to your phone where the Connect App (not Connect IQ) is at least running in the background.",
            "gps": {
                "title": "GPS Fix",
                "description": "If you see a message like \"GPS?\" on your watch, do a gps fix so that the watchface knows your position.",
                "steps": {
                    "step1": "Get in the starting screen of an activity like \"Running\" on the watch",
                    "step2": "Wait until your watch got a gps signal"
                }
            }
        }
    },
    "common": {
        "loading": "Loading...",
        "error": "Error",
        "retry": "Try again",
        "back": "Back",
        "close": "Close",
        "save": "Save",
        "cancel": "Cancel",
        "confirm": "Confirm",
        "notFound": "404 – Page not found"
    },
    "watchfacesOverview": {
        "title": "All Apps",
        "loading": "Loading apps...",
        "error": "Error loading apps",
        "retry": "Try again",
        "noWatchfaces": "No apps available"
    },
    "detailTabs": {
        "payment": "Payment",
        "themes": "Themes",
        "info": "Info",
        "instructions": "Instructions",
        "instructionsIntro": "All guides and instructions at a glance:",
        "noThemes": "No color themes available for this watchface."
    },
    "watchfaceDetail": {
        "loading": "Loading watchface...",
        "error": {
            "notFound": "Watchface not found",
            "loading": "Error loading watchface"
        },
        "backToOverview": "Back to overview",
        "goToStore": "Go to IQ Store",
        "goToTrial": "Trial Version",
        "devices": "devices",
        "info": {
            "title": "Features",
            "tides": "🌊 Tide information with automatic updates",
            "weather": "🌤️ Weather data from OpenWeatherMap and Yr",
            "colors": "🎨 Color themes and customizations",
            "location": "📍 GPS-based location determination",
            "faq": "❓ FAQ and support",
            "weatherFallback": "If an OpenWeatherMap Api key is entered without Once Call 3.0 subscription, there is a fallback to the \"Yr\" weather data provider."
        }
    },
    "themeInfo": {
        "howDoThemesWork": {
            "title": "How do Themes work?:",
            "description": "In the top of your watchface settings you have a \"themestring\" which represents your current color settings (for icons, chart, time etc) of your watchface. If you change something (like color of the top section) and press \"Save\", the Themestring will update itself the next time you open the settings. In this way you can save your color settings by copying and saving the themestring somewhere else. You can even share it with other people like in the reviews / forum for example and you can apply other peoples themestring to try out other styles."
        },
        "dynamicThemeSwitching": {
            "title": "Dynamic theme switching:",
            "sunset": {
                "title": "Switch after sunset / before sunrise -",
                "description": "if you put an exclamation mark ! in front of the themestring you want to switch to in the themestring field, it will be saved as the theme which is being switched to after sunset ( example: !UEUE[UEUEEEEEEU^ ). You can change it anytime by putting a themestring with a leading exclamation mark ! into the themestring field. If you want to delete the themestring for nighttime, just put a single exclamation mark ! in the themestring field and it won't switch anymore."
            },
            "lowPower": {
                "title": "Switch in Low Power -",
                "description": "it is the same process as setting a theme for sunset/sunrise above, but instead of an ! put a # in front of your themestring ( example: #UEUE[UEUEEEEEEU^ ). Every watch enters a \"Low Power Mode\" after ~10 seconds you are looking on the watch / coming back from a menu. Usually the seconds disappear in Low Power by default (can be changed in the watchface settings). Both switching options (@night/@LowPower) can be combined. Once set a dynamic themestring is not visible in your watchface settings so maybe make sure to save it somewhere else if you want to do some changes in the future maybe."
            }
        }
    },
    "colorThemes": {
        "clickToCopy": "Click to copy",
        "clickToPreview": "Click to preview",
        "closePreview": "Close preview",
        "copied": "Copied!",
        "useInSettings": "Use it in your watchface settings",
        "guideLink": "More information about themes & themestrings",
        "deviceNote": "Note: Color themes are not available on all devices.",
        "noFieldNote": "If your watchface settings do not have a separate field for color themes, they are not supported for your watch model on this watchface."
    },
    "payment": {
        "option1": {
            "title": "Payment Option 1: Garmin Pay",
            "description": "Purchase the paid watchface directly via Garmin Pay on the ConnectIQ store (if your watch is supported).",
            "link": "Link to ConnectIQ Store"
        },
        "option2": {
            "title": "Payment Option 2: Direct Payment via WatchfaceID",
            "description": "If your watch is not supported by the paid pro version, you can purchase a trial/VX version key directly by entering your WatchfaceID.",
            "vxNote": "This is a key for the VX Version of the watchface, not the Garmin Pay version!",
            "vxLink": "Link to VX Version"
        },
        "tax": "tax",
        "wfid": {
            "description": "Enter your WatchfaceID (found in your watchface settings)",
            "placeholder": "Enter WatchfaceID (5-6 digits)",
            "guideLink": "How to find your WatchfaceID",
            "guideLinkShort": "Show instructions"
        },
        "wfidError": {
            "letters": "WatchfaceID should only contain numbers",
            "tooLong": "WatchfaceID should be 5-6 digits long",
            "invalid": "Please enter a valid WatchfaceID (5-6 digits)",
            "lengthHint": "A WatchfaceID is usually 5-6 digits. Please double-check."
        },
        "errors": {
            "empty": "Please enter your WatchfaceID",
            "invalid": "Please enter a valid WatchfaceID",
            "config": "Payment configuration error",
            "checkout": "Failed to open checkout. Please try again."
        },
        "buy": "Buy Now",
        "selectMethod": "Choose your version",
        "versionSelectHint": "These are two separate versions — please select the correct one:",
        "vxWarning": "The VX and Elite versions are each separately downloadable versions of the watchface. An activation code for the VX version does NOT unlock the Elite version.",
        "beta": {
            "title": "Currently in Beta",
            "description": "During this beta phase, you can use this watchface completely for free. There will be no automatic charges after the test phase. New features and functions are being tested during this time. Thanks for your feedback!",
            "descriptionApp": "During this beta phase, you can use this app completely for free. There will be no automatic charges after the test phase. New features and functions are being tested during this time. Thanks for your feedback!"
        },
        "elite": {
            "badge": "Garmin Pay",
            "title": "Elite Version",
            "description": "This watchface is available exclusively via Garmin Pay on the ConnectIQ Store.",
            "comboDescription": "Purchase directly via Garmin Pay on the ConnectIQ Store. This is a separate, standalone version.",
            "compatibility": "Note: Not all watch models support Garmin Pay. Check your device compatibility on the store page. Otherwise download the VX version (technically identical).",
            "storeLink": "Open in ConnectIQ Store",
            "moreGuide": "Need more details about the difference between Elite and VX versions?",
            "moreGuideLink": "Read the purchase & installation guide",
            "eliteNoKey": "When purchasing an Elite version via Garmin Pay, no unlock code is sent. The Elite version is unlocked directly after purchase and does not require a code.",
            "accountBound": "The purchase via Garmin Pay is bound to your Garmin account and automatically available for your future devices (if compatible).",
            "eliteNoKeyAndGuide": "When purchasing via Garmin Pay, no unlock code is sent — the Elite version is unlocked directly after purchase. More details can be found in the purchase & installation guide."
        },
        "vx": {
            "badge": "All Devices",
            "title": "VX Version",
            "description": "Compatible with all supported devices. Download the free trial from the ConnectIQ Store, then activate it here with a purchased code.",
            "trialNote": "The VX version includes a free trial period. After the trial, purchase an activation code below to unlock it permanently.",
            "deviceBound": "An activation code is bound to your device and unlocks the VX version permanently.",
            "moreGuide": "Need more details about the VX version, keys or installation?",
            "moreGuideLink": "Read the purchase & installation guide",
            "downloadLink": "Download VX Version",
            "details": {
                "line1": "After successful payment, the activation key will be sent to the email address you enter in the next step.",
                "line2": "Please make sure that the email address is correct.",
                "line3": "Please check your spam folder after a successful payment – key emails often land there.",
                "line4": "All payment options are handled by Paddle – no account necessary.",
                "line5": "For support requests, use the contact form or the ConnectIQ Store tool.",
                "line6": "Other inquiries cannot be processed."
            }
        },
        "standalone": {
            "title": "Purchase Activation Code",
            "description": "Purchase an activation code by entering your WatchfaceID below. The code will be sent to you immediately."
        },
        "checkoutOpening": "Opening checkout...",
        "info": {
            "key": "After purchase, you will receive a key that you can enter in your watchface settings.",
            "activation": "The key will be activated immediately after successful payment.",
            "support": "If you have any questions, please contact support.",
            "moreGuide": "More details about purchase & installation"
        },
        "methods": {
            "applePay": "Apple Pay",
            "googlePay": "Google Pay",
            "alipay": "Alipay",
            "paypal": "PayPal",
            "creditCard": "Credit Card"
        }
    },
    "fonts": {
        "title": "Fonts",
        "timefonts": "Time Fonts",
        "datafonts": "Data Fonts"
    },
    "weather": {
        "title": "Weather Data Providers",
        "intro": "For many watch faces, there is a selection of different weather data providers on most devices. The weather data provider can be changed in the settings of the ConnectIQ app. In most cases, Garmin weather or Yr is selected as the weather data provider by default.",
        "noData": "If a weather data field displays 'no data', no weather data could be loaded. Below you will find the respective steps to get weather data on the watch.",
        "tabs": {
            "garmin": "Garmin",
            "yr": "Yr",
            "openweathermap": "OpenWeatherMap",
            "visualcrossing": "Visual Crossing",
            "location": "Location"
        },
        "garmin": {
            "title": "Using Garmin Weather:",
            "description": "If the device supports Garmin weather, all you need to do is connect the Garmin watch to a phone via Bluetooth and make sure the Connect app (not Connect IQ) is at least running in the background and has access to your phone's current location. (The current location (GPS) must be switched on)",
            "download": "The watch will then automatically download the weather data from this service, which may take a few minutes.",
            "check": "You can also check whether the internal Garmin weather widget on the watch is already displaying weather data. If no data can be seen there, the watch face cannot display it either as it is the same data source.",
            "noApiKey": "No extra API key is required for Garmin Weather.",
            "location": "With some watch faces, the respective supplied location is also displayed when using Garmin Weather. With Garmin weather stations, this is usually the nearest aerodrome - a more precise indication of the location is only possible with other weather data providers.",
            "update": "The location is updated as soon as the location data is active on the phone.",
            "noData": "If no weather data can be displayed or updated, the message 'no data' is displayed -> carry out the above steps.",
            "missingData": {
                "title": "Data not included in Garmin Weather:",
                "airQuality": "Air quality",
                "uvIndex": "UV index",
                "description": "Weather description"
            }
        },
        "yr": {
            "title": "Using Yr:",
            "description": "Yr is a service provided by the Norwegian Meteorological Institute and NRK and can be used without an API key.",
            "setup": "To obtain weather data from Yr, select it as the weather data provider and connect the watch to the phone via Bluetooth.",
            "connectApp": "On the phone, make sure that the Connect app (not ConnectIQ) is at least running in the background - the watch uses it to download weather data from the Internet, among other things.",
            "interval": "If the watch face has a function for setting the update interval, a minimum of 10 minutes is possible with Yr as the weather data provider.",
            "noData": {
                "title": "no data / wrong location:",
                "description": "If no weather data is loaded or the displayed location is incorrect, this can easily be rectified by updating the saved location of the watch face.",
                "fix": "To do this, go to the start screen of an activity such as 'Running' on the watch and wait until the watch has a GPS signal. The weather data / location should then update within minutes."
            },
            "missingData": {
                "title": "Data that is not included in Yr:",
                "airQuality": "Air quality"
            }
        },
        "openweathermap": {
            "title": "Using OpenWeatherMap:",
            "description": "If your watchface supports OpenWeatherMap as weather data provider and you want to use it, you need a free API key which you can get on their website:",
            "setup": "As soon as you got the key you can enter it in the specific field in your watchface settings. (it might take up to a few hours until a freshly generated key actually works)",
            "connect": "Be sure the watch is connected to your phone via bluetooth and the Connect App (not Connect IQ) is at least running in the background to let it download weather data.",
            "gps": "If you see a message like \"GPS?\" on your watch, do a gps fix so that the watchface knows your position. (Get in the starting screen of an activity like \"Running\" and wait until your watch got a gps signal)",
            "uv": {
                "title": "UV-Index / Precipitation on OpenWeatherMap:",
                "oldKey": "If you have an older API key (generated before ~Feb23) you can use this metrics without any problems.",
                "newKey": "If you generated your API key after that date and want to see these metrics provided by OpenWeatherMap you might need an additional (free) subscription, namely the \"One Call by Call\" subscription plan.",
                "limit": "The first 1000 requests per day based on this subscription are basically free and you can set the limit to 1000 requests which are more than enough to provide weatherdata for the whole day on any of my watchfaces."
            },
            "select": "If the watchface supports more than one 3rd party weather provider make sure to select OpenWeatherMap as the current weather provider in the dropdown menu in your watchface settings."
        },
        "visualcrossing": {
            "title": "Using Visual Crossing:",
            "description": "If you want to use Visual Crossing you need to go on their website and get a free api key.",
            "setup": "As soon as you got your key you can enter it in the specific field in your watchface settings and select Visual Crossing as your weather data provider in the dropdown menu.",
            "connect": "Be sure the watch is connected to your phone via bluetooth and the Connect App (not Connect IQ) is at least running in the background to let it download weather data.",
            "gps": "If you see a message like \"GPS?\" on your watch, do a gps fix so that the watchface knows your position. (Get in the starting screen of an activity like \"Running\" and wait until your watch got a gps signal)"
        },
        "location": {
            "title": "Location Update",
            "description": "To display weather data correctly, the watchface needs your current location. This is determined via GPS.",
            "gpsFix": {
                "title": "Perform GPS Fix:",
                "description": "If you see a message like \"GPS?\" on your watch, you need to perform a GPS fix so that the watchface knows your position.",
                "step1": "Go to the start screen of an activity like \"Running\" on the watch",
                "step2": "Wait until the watch receives a GPS signal",
                "step3": "Return to the watchface - the weather data should update within minutes"
            },
            "requirements": {
                "title": "Requirements:",
                "bluetooth": "The watch must be connected to the phone via Bluetooth",
                "connectApp": "The Connect App (not Connect IQ) must be running at least in the background",
                "gps": "GPS must be enabled on the phone"
            }
        }
    },
    "bierwander": {
        "title": "Bierwandern",
        "intro": "The Bierwandern app lets you track your hike and the refills you enjoy along the way. On top of that, there is a blood alcohol estimate with a chart (rough estimate) – but please don't take it too seriously!",
        "settingsHint": "On the start screen, press the menu button on your watch to open the settings menu (on watches with a 2-button layout, long-press the touchscreen).",
        "disclaimer": "The BAC calculation is purely for fun – an algorithm that estimates blood alcohol based on drinks consumed and body weight! Do not make any decisions based on this value; it is not reliable and is not an actual measurement of your alcohol level!",
        "closing": "Otherwise, I wish you great hikes and a good time!",
        "support": "For problems or suggestions, please use the \"App Support\" button – thank you."
    },
    "bodysurf": {
        "intro": {
            "title": "What is BodySurf?",
            "text": "BodySurf is a Garmin Connect IQ watch app for bodysurfing. It uses GPS to detect when you ride a wave, measures distance and duration of each ride, and gives you a fun title based on how far you went. Sessions are saved as activities with individual laps in Garmin Connect."
        },
        "gps": {
            "title": "Before you start – GPS",
            "text1": "Wait until the watch shows a GPS fix before starting a session. On the start screen you will see \"Searching GPS\" until a signal is available, then \"START to begin\".",
            "text2": "For best accuracy the app uses multi-GNSS / dual-band GPS when your watch supports it. Stand still for a moment near the water if the fix takes a while."
        },
        "start": {
            "title": "Start Screen",
            "startBtn": "Begin a new session once GPS is ready (START = begin session).",
            "menuBtn": "Open settings: How to use and Units (MENU = settings / info).",
            "backBtn": "BACK = exit app"
        },
        "during": {
            "title": "During Activity",
            "detect": "Rides are detected by GPS speed automatically. Use BACK to start/stop a ride manually. Short movements under about 2 metres are discarded.",
            "startBtn": "START = stop / save / resume",
            "backBtn": "BACK = manual ride on/off",
            "live": "While riding you see the current distance, a live title, and \"RIDING!\". After a ride ends the last title and stats stay visible until the next ride starts."
        },
        "stop": {
            "title": "Stop menu",
            "resume": "Resume",
            "save": "Save",
            "discard": "Discard"
        },
        "after": {
            "title": "After Session",
            "summary": "The summary shows session time, rides count, total distance, and your best ride with title and stats.",
            "rides": "Swipe to see ride details. On button watches use << DOWN rides.",
            "finished": "When done: New Session with START or Exit App with BACK."
        },
        "titles": {
            "title": "Ride titles",
            "intro": "Each ride gets a fun title based on distance. The longer the ride, the better the title!",
            "colDistance": "Distance",
            "colTitle": "Title",
            "th0": "Beach Bum",
            "th1": "Sea Cucumber",
            "th2": "Flounder",
            "th3": "Seagull Repellent",
            "th4": "Beach Sheriff",
            "th5": "Surf Patrol",
            "th6": "Wave Rider",
            "th7": "Current Advisor",
            "th8": "Wave Breaker",
            "th9": "Aquaman",
            "th10": "Human Surfboard",
            "th11": "Coastal Legend",
            "th12": "Poseidon"
        },
        "connect": {
            "title": "Garmin Connect",
            "text": "Saved sessions appear in Garmin Connect with custom fields: ride title, lap distance (metres), lap time (seconds), and longest ride distance/time at session level. Each detected ride is stored as a lap."
        },
        "instinct": {
            "title": "Instinct 2 / 3 (MIP)",
            "layout": "On Instinct watches the sub-display shows time, GPS, and ride distance. Use the DOWN button instead of swipe (<< DOWN rides).",
            "menu": "Menu is shown on the left of the start screen. Press START when GPS OK appears in the sub-display."
        },
        "settings": {
            "title": "Settings",
            "units": "Units: switch between metric and imperial for distance and speed on the watch.",
            "info": "Info: onboard help (How to use) for buttons during activity, after session, and on the start screen."
        },
        "supportBefore": "For problems or suggestions please use the",
        "supportLink": "contact form",
        "supportAfter": " – thank you!"
    }
};
